/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import "../styles/Banner.css";
import "../styles/Login.css";
import { toast } from "react-toastify";

const Banner = () => {
  const [showModal, setShowModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    mobileNumber: "",
    schoolName: "",
    password: "",
    confirmPassword: "",
    userType: "",
    fullname: "",
  });
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [resetValidationErrors, setResetValidationErrors] = useState({});
  const navigate = useNavigate();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/; // Accepts 10 digits
    return phoneRegex.test(phone);
  };

  const validatePassword = (password) => {
    return password.length >= 6; // Minimum 6 characters
  };

  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      errors.password = "Password must be at least 6 characters long";
    }

    // Registration-specific validations
    if (!isLogin) {
      // Phone number validation
      if (!formData.mobileNumber) {
        errors.mobileNumber = "Mobile number is required";
      } else if (!validatePhone(formData.mobileNumber)) {
        errors.mobileNumber = "Please enter a valid phone number (10 digits)";
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        errors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }

      // Full name validation
      if (!formData.fullname.trim()) {
        errors.fullname = "Full name is required";
      }

      // School name validation
      if (!formData.schoolName.trim()) {
        errors.schoolName = "School name is required";
      }

      // User type validation
      if (!formData.userType) {
        errors.userType = "Please select if you are a student or teacher";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Real-time validation
    const errors = { ...validationErrors };
    
    switch (field) {
      case 'email':
        if (!value) {
          errors.email = "Email is required";
        } else if (!validateEmail(value)) {
          errors.email = "Please enter a valid email address";
        } else {
          errors.email = "";
        }
        break;
        
      case 'password':
        if (!value) {
          errors.password = "Password is required";
        } else if (!validatePassword(value)) {
          errors.password = "Password must be at least 6 characters long";
        } else {
          errors.password = "";
        }
        // Also check confirm password if it exists
        if (formData.confirmPassword && value !== formData.confirmPassword) {
          errors.confirmPassword = "Passwords do not match";
        } else if (formData.confirmPassword && value === formData.confirmPassword) {
          errors.confirmPassword = "";
        }
        break;
        
      case 'confirmPassword':
        if (!isLogin) {
          if (!value) {
            errors.confirmPassword = "Please confirm your password";
          } else if (formData.password !== value) {
            errors.confirmPassword = "Passwords do not match";
          } else {
            errors.confirmPassword = "";
          }
        }
        break;
        
      case 'mobileNumber':
        if (!isLogin) {
          if (!value) {
            errors.mobileNumber = "Mobile number is required";
          } else if (!validatePhone(value)) {
            errors.mobileNumber = "Please enter a valid phone number (10 digits)";
          } else {
            errors.mobileNumber = "";
          }
        }
        break;
        
      case 'fullname':
        if (!isLogin) {
          if (!value.trim()) {
            errors.fullname = "Full name is required";
          } else {
            errors.fullname = "";
          }
        }
        break;
        
      case 'schoolName':
        if (!isLogin) {
          if (!value.trim()) {
            errors.schoolName = "School name is required";
          } else {
            errors.schoolName = "";
          }
        }
        break;
        
      case 'userType':
        if (!isLogin) {
          if (!value) {
            errors.userType = "Please select if you are a student or teacher";
          } else {
            errors.userType = "";
          }
        }
        break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
  };

  // Reset password validation
  const validateResetPasswords = (newPass, confirmPass) => {
    const errors = {};
    
    if (!newPass) {
      errors.newPassword = "New password is required";
    } else if (!validatePassword(newPass)) {
      errors.newPassword = "Password must be at least 6 characters long";
    }
    
    if (!confirmPass) {
      errors.confirmPassword = "Please confirm your password";
    } else if (newPass !== confirmPass) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    setResetValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Prepare the user data based on whether it's a login or registration
    const userData = {
      email: formData.email.toLowerCase(),
      mobileNumber: formData.mobileNumber,
      schoolName: formData.schoolName,
      userType: formData.userType,
      password: formData.password,
      fullname: formData.fullname,
    };
 
    try {
      const endpoint = isLogin ? "/api/users/login" : "/api/users/register";
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_APP_URL}${endpoint}`,
        userData
      );

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        setShowModal(false);
        navigate("/home");
        handleNewChatAfterLogin();
      }
    } catch (error) {
      console.error("Error during registration/login:", error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Authentication failed. Please try again.."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowModal(false);
    setShowEmailPopup(true);
  };

  const closeAllModals = () => {
    setShowModal(false);
    setShowEmailPopup(false);
    setShowPasswordPopup(false);
    setError("");
    setValidationErrors({});
    setResetValidationErrors({});
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_APP_URL}/api/users/forgot-password`,
        { email }
      );
      if (response.data.success) {
        setShowEmailPopup(false);
        setShowPasswordPopup(true);
        setError("");
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setError("An error occurred. Please try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate reset passwords
    if (!validateResetPasswords(newPassword, confirmPassword)) {
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_APP_URL}/api/users/reset-password`,
        { email, newPassword }
      );
      if (response.data.success) {
        toast.success("Password reset successfully. You can now log in.");
        closeAllModals();
        setShowModal(true); // Reopen the login modal
        setIsLogin(true);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setError("An error occurred. Please try again.");
    }
  };

  const handleNewChatAfterLogin = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user ? user.id : "Not available";

    if (!userId) {
      console.error("User ID is not available. Cannot create new chat.");
      return;
    }

    // Fetch chat history to check the count
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_APP_URL}/api/chats/history`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const chatHistory = res.data; // Assuming this is the chat history array

      // Check if chat history limit is reached
      if (chatHistory.length >= 8) {
        toast.error(
          "Your chat threads have reached the maximum limit. Please delete any unwanted chat threads from your history."
        );
        // Optionally, redirect to the 4th chat if it exists
        if (chatHistory.length > 0) {
          const fourthChat = chatHistory[3]; // Get the 4th chat (index 3)
          // handleChatSelect(fourthChat); // Uncomment if you have this function
        }
        return; // Prevent further execution
      }

      const newThreadTitle = `Chat Thread - ${new Date().toLocaleString()}`;

      const newChatRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_APP_URL}/api/users/new-chat`,
        {
          title: newThreadTitle,
          userId: userId,
          initialMessage: "Welcome to the IFRS Knowledge Bot Your intelligent companion for Sri Lanka Accounting Standards. Whether you're a student navigating complex standards or a teacher seeking precise references, I'm here to simplify accounting knowledge. Let's explore LKAS together! 💡",
          role: "assistant",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // New code to open the chatbox and send the initial message
      openChatbox(newChatRes.data._id); // Function to open the chatbox with the new chat ID
      sendMessage(newChatRes.data._id, "Welcome to the IFRS Knowledge Bot Your intelligent companion for Sri Lanka Accounting Standards. Whether you're a student navigating complex standards or a teacher seeking precise references, I'm here to simplify accounting knowledge. Let's explore LKAS together! 💡"); // Function to send the initial message

      localStorage.setItem("selectedChatId", newChatRes.data._id);
      // fetchChatHistory();
    } catch (error) {
      console.error("Error creating new chat:", error);
    }
  };

  // New functions to handle chatbox opening and message sending
  const openChatbox = (chatId) => {
    // Logic to open the chatbox with the given chatId
    console.log(`Opening chatbox for chat ID: ${chatId}`);
    // Implement your chatbox opening logic here
  };

  const sendMessage = (chatId, message) => {
    // Logic to send a message to the chatbox
    console.log(`Sending message to chat ID ${chatId}: ${message}`);
    // Implement your message sending logic here
  };

  const handleClick = () => {
    window.open(
      "https://www.accaglobal.com/gb/en/footertoolbar/privacy/acca-website-privacy-promise.html",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <Header />
      <div
        className="banner"
        style={{
          backgroundImage: `url('/banner.png')`,
        }}
      >
        <div className="content-overlay">
          <div className="content-wrapper">
            <div className="card-container">
              <div className="red-card">
                <h2 className="card-title">
                  Welcome to the
                  <br />
                  IFRS Knowledge Bot
                </h2>
                <p className="card-text">
                The IFRS Knowledge Bot is an AI-powered learning companion designed specifically for A/L teachers and students. Teachers can leverage the bot to access teaching resources and create engaging lesson plans while students can benefit from getting access to practice questions and solutions. With the IFRS Knowledge Bot, learning finance becomes more interactive, efficient, and enjoyable.
                </p>
                <button
                  className="card-button secondary-button"
                  onClick={() => {
                    setIsLogin(false);
                    setShowModal(true);
                  }}
                >
                  Sign up
                </button>
                <button
                  className="card-button secondary-button"
                  onClick={() => {
                    setIsLogin(true);
                    setShowModal(true);
                  }}
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              ×
            </button>
            <div className="auth-box">
              <h2>
                {isLogin
                  ? "Log in to the IFRS Knowledge Bot"
                  : "Sign up for the IFRS Knowledge Bot"}
              </h2>
              <p className="title">
                Insert your email and password below to access your secure
                personal account.
              </p>

              {error && <div className="auth-error">{error}</div>}

              <form onSubmit={handleSubmit} className="auth-form">
                {!isLogin && (
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={formData.fullname}
                      onChange={(e) => handleInputChange("fullname", e.target.value)}
                      required
                      className={validationErrors.fullname ? "error" : ""}
                    />
                    {validationErrors.fullname && (
                      <span className="validation-error">{validationErrors.fullname}</span>
                    )}
                  </div>
                )}
                
                <div className="form-group">
                  <label>Email address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onKeyDown={handleKeyDown}
                    required
                    className={validationErrors.email ? "error" : ""}
                  />
                  {validationErrors.email && (
                    <span className="validation-error">{validationErrors.email}</span>
                  )}
                </div>

                {!isLogin && (
                  <>
                    <div className="form-group">
                      <label>Mobile Number</label>
                      <input
                        type="text"
                        value={formData.mobileNumber}
                        onChange={(e) => handleInputChange("mobileNumber", e.target.value)}
                        required
                        placeholder="Enter 10 digit phone number"
                        className={validationErrors.mobileNumber ? "error" : ""}
                      />
                      {validationErrors.mobileNumber && (
                        <span className="validation-error">{validationErrors.mobileNumber}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>School Name</label>
                      <input
                        type="text"
                        value={formData.schoolName}
                        onChange={(e) => handleInputChange("schoolName", e.target.value)}
                        required
                        className={validationErrors.schoolName ? "error" : ""}
                      />
                      {validationErrors.schoolName && (
                        <span className="validation-error">{validationErrors.schoolName}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>User Type</label>
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="userType"
                            value="student"
                            checked={formData.userType === "student"}
                            onChange={(e) => handleInputChange("userType", e.target.value)}
                          />
                          Student
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="userType"
                            value="teacher"
                            checked={formData.userType === "teacher"}
                            onChange={(e) => handleInputChange("userType", e.target.value)}
                          />
                          Teacher
                        </label>
                      </div>
                      {validationErrors.userType && (
                        <span className="validation-error">{validationErrors.userType}</span>
                      )}
                    </div>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group column">
                    <label>Password</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      onKeyDown={handleKeyDown}
                      required
                      className={validationErrors.password ? "error" : ""}
                    />
                    {validationErrors.password && (
                      <span className="validation-error">{validationErrors.password}</span>
                    )}
                    <div className="forgot-password">
                      <button
                        className="forgot-pass"
                        onClick={handleForgotPasswordClick}
                      >
                        {isLogin ? "forgotten your password?" : ""}
                      </button>
                    </div>
                  </div>
                  {!isLogin && (
                    <div className="form-group column">
                      <label>Confirm Password</label>
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                        required
                        className={validationErrors.confirmPassword ? "error" : ""}
                      />
                      {validationErrors.confirmPassword && (
                        <span className="validation-error">{validationErrors.confirmPassword}</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="auth-footer">
                  <button 
                    type="submit" 
                    className="auth-submit"
                    disabled={loading}
                  >
                    {loading ? "Please wait..." : (isLogin ? "Log in" : "Sign up")}
                  </button>
                  <p>
                    {isLogin
                      ? "Don't have an account?"
                      : "Already have an account?"}
                    <button
                      className="sign-up"
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setValidationErrors({});
                      }}
                    >
                      {isLogin ? "Sign up" : "Log in"}
                    </button>
                  </p>
                </div>

                <div className="privacy-policy">
                  <button className="term" onClick={handleClick}>
                    View our data and privacy policy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Email Popup */}
      {showEmailPopup && (
        <div className="modal-overlay" onClick={() => setShowEmailPopup(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Forgot Password</h2>
            <form onSubmit={handleEmailSubmit}>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={error && !validateEmail(email) ? "error" : ""}
                />
              </div>
              {error && <p className="error">{error}</p>}
              <div className="button-row">
                <button type="submit">Submit</button>
                <button type="button" onClick={() => setShowEmailPopup(false)}>
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Password Popup */}
      {showPasswordPopup && (
        <div
          className="modal-overlay"
          onClick={() => setShowPasswordPopup(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Reset Password</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    validateResetPasswords(e.target.value, confirmPassword);
                  }}
                  required
                  className={resetValidationErrors.newPassword ? "error" : ""}
                />
                {resetValidationErrors.newPassword && (
                  <span className="validation-error">{resetValidationErrors.newPassword}</span>
                )}
              </div>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    validateResetPasswords(newPassword, e.target.value);
                  }}
                  required
                  className={resetValidationErrors.confirmPassword ? "error" : ""}
                />
                {resetValidationErrors.confirmPassword && (
                  <span className="validation-error">{resetValidationErrors.confirmPassword}</span>
                )}
              </div>
              {error && <p className="error">{error}</p>}
              <div className="button-row">
                <button type="submit">Submit</button>
                <button
                  type="button"
                  onClick={() => setShowPasswordPopup(false)}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Banner;